import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { AlertTriangle, RotateCcw, Loader2, Shield, Database, Download, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import WadahSyncPanel from "@/components/admin/WadahSyncPanel";
import TwilioPanel from "@/components/admin/TwilioPanel";

export default function AdminSystem() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [resetting, setResetting] = useState(false);
  const [envInfo, setEnvInfo] = useState(null);
  const REQ = "HAPUS SEMUA DATA DEMO";

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: e }] = await Promise.all([
        api.get("/admin/system/demo-stats"),
        api.get("/system/env"),
      ]);
      setStats(s); setEnvInfo(e);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (confirmPhrase.trim() !== REQ) {
      toast.error(`Ketik dengan tepat: ${REQ}`);
      return;
    }
    if (!window.confirm("Ini akan MENGHAPUS PERMANEN semua data demo. Lanjutkan?")) return;
    setResetting(true);
    try {
      const { data } = await api.post("/admin/system/reset-to-production", {
        confirm_phrase: confirmPhrase.trim(),
      });
      toast.success(`Berhasil dihapus ${data.total} dokumen di ${Object.keys(data.deleted).length} koleksi`);
      setConfirmPhrase("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setResetting(false); }
  };

  if (user && user.role !== "SUPER_ADMIN") return <Navigate to="/403" replace />;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-system-root">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground font-semibold">Sistem</div>
          <h1 className="font-heading font-black text-3xl mt-1">Konfigurasi Produksi</h1>
          <p className="text-sm text-muted-foreground mt-1">Kelola environment, purge data demo, dan cek status backup.</p>
        </div>

        {/* Env Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>Status Environment</CardTitle>
                <CardDescription>Konfigurasi banner dan mode operasi</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {envInfo ? (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Mode</div>
                  <div className="mt-1 font-bold text-lg" data-testid="env-mode">{envInfo.env}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Pilot Mode</div>
                  <div className="mt-1 font-bold text-lg">{envInfo.pilot_mode ? "AKTIF" : "TIDAK AKTIF"}</div>
                </div>
                <div className="col-span-2 text-xs text-muted-foreground bg-muted/40 rounded-md p-3 border border-border">
                  Ubah <code>SIMPEL_ENV</code> di <code>/app/backend/.env</code> menjadi <code>PILOT</code> atau <code>PRODUCTION</code> lalu restart backend.
                </div>
              </div>
            ) : <div>Memuat…</div>}
          </CardContent>
        </Card>

        {/* Backup Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>Backup Database</CardTitle>
                <CardDescription>Jalankan sebelum reset production atau perubahan besar</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-sm space-y-2">
              <div>Backup manual di server:</div>
              <pre className="bg-muted/60 rounded-md p-3 text-xs font-mono">bash /app/scripts/backup_mongo.sh</pre>
              <div className="text-xs text-muted-foreground">Hasil di <code>/app/backups/simpel-YYYY-MM-DD_HHMM.tar.gz</code> (retensi 30 file).</div>
            </div>
          </CardContent>
        </Card>

        {/* Email Delivery Diagnostics */}
        <EmailDiagnostics />

        {/* Wadah Sync — Sinkronkan struktur pengurus canonical ke DB */}
        <WadahSyncPanel />

        {/* Twilio SMS / WhatsApp — MOCKED sampai kredensial diisi */}
        <TwilioPanel />

        {/* Danger Zone: Reset to Production */}
        <Card className="border-destructive/40">
          <CardHeader>
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <div>
                <CardTitle className="text-destructive">Reset ke Production</CardTitle>
                <CardDescription>Hapus PERMANEN semua data operasional dummy (announcements, keputusan, surat, kalender, program, kas, anggota, iuran, notifikasi, wadah kas & undian).</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {envInfo?.env === "PRODUCTION" && (
              <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3">
                <div className="font-bold mb-1">🔒 Dinonaktifkan di PRODUCTION</div>
                Untuk mencegah kecelakaan, endpoint ini diblok saat <code>SIMPEL_ENV=PRODUCTION</code>. Bila perlu purge: set env ke <code>PILOT</code> sementara, restart backend, purge, lalu kembalikan ke <code>PRODUCTION</code>.
              </div>
            )}
            <div className="text-sm bg-destructive/5 border border-destructive/20 rounded-md p-3">
              <div className="font-bold mb-1 text-destructive">⚠️ Peringatan</div>
              Struktur (pengguna, jemaat, role, audit log) TETAP dipertahankan. Data operasional demo akan dihapus. Lakukan <strong>backup</strong> lebih dulu.
            </div>

            {loading ? (
              <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Memuat statistik…</div>
            ) : stats ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-4 w-4" />
                  <div className="font-bold text-sm">Total dokumen demo: <span data-testid="demo-total">{stats.total.toLocaleString('id-ID')}</span></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-xs">
                  {Object.entries(stats.stats).filter(([,v]) => v > 0).map(([k, v]) => (
                    <div key={k} className="flex justify-between bg-muted/40 rounded px-2 py-1">
                      <span className="font-mono text-muted-foreground">{k}</span>
                      <span className="font-bold">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-2 pt-2 border-t border-border">
              <Label htmlFor="phrase">Ketik <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-xs">{REQ}</code> untuk konfirmasi</Label>
              <Input
                id="phrase"
                value={confirmPhrase}
                onChange={(e) => setConfirmPhrase(e.target.value)}
                placeholder={REQ}
                className="font-mono"
                data-testid="input-confirm-reset"
              />
              <Button
                variant="destructive"
                disabled={resetting || confirmPhrase.trim() !== REQ || envInfo?.env === "PRODUCTION"}
                onClick={doReset}
                data-testid="btn-reset-production"
              >
                {resetting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                {resetting ? "Menghapus…" : "Reset ke Production"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}


function EmailDiagnostics() {
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [failures, setFailures] = useState(null);
  const [loadingFailures, setLoadingFailures] = useState(false);

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Email tidak valid"); return; }
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/admin/system/test-email", { to: testTo });
      setResult({ ok: true, id: data.provider_id, to: data.sent_to });
      toast.success(`Email test dikirim (ID: ${data.provider_id?.slice(0, 8)}…). Cek Inbox + Spam.`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setResult({ ok: false, error: msg });
      toast.error(msg);
    } finally { setSending(false); }
  };

  const loadFailures = async () => {
    setLoadingFailures(true);
    try {
      const { data } = await api.get("/admin/system/email-failures?limit=20");
      setFailures(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setLoadingFailures(false); }
  };

  return (
    <Card data-testid="email-diagnostics-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Mail className="h-5 w-5 text-primary" />
          <div>
            <CardTitle>Diagnostik Email</CardTitle>
            <CardDescription>Kirim email uji untuk verifikasi pipeline transactional (Emergent Resend). API key disimpan di server; tidak pernah di frontend.</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="testto">Email Tujuan Uji</Label>
          <div className="flex gap-2">
            <Input
              id="testto" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="nama@domain.com" className="flex-1"
              data-testid="input-test-email-to"
            />
            <Button onClick={sendTest} disabled={sending || !testTo} data-testid="btn-send-test-email">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
              Kirim Test
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">Sender: <code>SIMPELKAMI GPDP</code> via Emergent Email Proxy (Resend). Subject: "[TEST] Verifikasi Email SIMPELKAMI GPDP".</div>
        </div>

        {result && (
          <div
            className={`text-sm rounded-md p-3 border ${result.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-destructive/10 border-destructive/20 text-destructive"}`}
            data-testid="test-email-result"
          >
            {result.ok ? (
              <>✅ <strong>Terkirim.</strong> Provider ID: <code className="font-mono text-xs">{result.id}</code>. Recipient: <strong>{result.to}</strong>. Cek Inbox + Spam.</>
            ) : (
              <>❌ <strong>Gagal:</strong> {result.error}</>
            )}
          </div>
        )}

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              Log Kegagalan Email
            </div>
            <Button size="sm" variant="outline" onClick={loadFailures} disabled={loadingFailures} data-testid="btn-load-email-failures">
              {loadingFailures ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              {failures ? "Refresh" : "Muat"}
            </Button>
          </div>
          {failures ? (
            failures.total === 0 ? (
              <div className="text-xs text-muted-foreground italic">Tidak ada kegagalan email tercatat. 🎉</div>
            ) : (
              <div className="space-y-1 text-xs" data-testid="email-failures-list">
                <div className="text-muted-foreground mb-1">Total: {failures.total} kegagalan (menampilkan 20 terakhir)</div>
                {failures.items.map((f) => (
                  <div key={f.id} className="bg-muted/40 rounded px-2 py-1.5 border border-border">
                    <div className="flex justify-between">
                      <span className="font-mono">{f.recipient}</span>
                      <span className="text-muted-foreground">{new Date(f.timestamp).toLocaleString("id-ID")}</span>
                    </div>
                    <div className="text-muted-foreground mt-0.5">{f.kind} · HTTP {f.status_code}: {f.detail?.slice(0, 100)}</div>
                  </div>
                ))}
              </div>
            )
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
